/* eslint-disable */
/**
 * COPIED VERBATIM from src/app/components/direct/data/rvpFixtures.js (commit 7772aebb).
 *
 * No operating-mode coupling, so it needed no adaptation. It is the grounded model — it mirrors
 * the admin module at /admin/rvp-qc, so a merchant's setup here is the same object ops configures
 * rather than a merchant-side copy of it. That is exactly why it is copied rather than reinvented:
 * the master parameter list is ProShip's constant QC vocabulary, and each courier's own QC
 * parameters map onto these ids behind the scenes.
 *
 * QC is configured PER MERCHANT and applies to every reverse pickup on every courier — the one
 * setup dimension in this module that is not per courier account.
 */
/**
 * Prozo Direct — RVP / doorstep-QC configuration model.
 *
 * Mirrors the admin module at /admin/rvp-qc exactly, because the merchant screen and the
 * ops screen must be the same object seen from two sides. Sources:
 *   proship_admin_panel src/app/pages/admin/rvp-qc/{rvpQcMockData,RvpQcEditor}.js
 *
 * FOUR THINGS CARRIED OVER VERBATIM, because getting any of them wrong would make the
 * merchant's setup unmergeable with what ops already runs:
 *
 * 1. MASTER PARAMETERS are courier-independent. They are ProShip's constant QC vocabulary;
 *    each courier's own QC parameters map onto these by id. A merchant picks checks by
 *    these names and never sees a courier's internal parameter codes.
 * 2. QC is configured PER MERCHANT and applies globally to every reverse pickup across
 *    every courier — admin says so in its own tooltip. It is therefore the one setup
 *    dimension in this module that is NOT per courier account.
 * 3. THREE QC TYPES: standard, reason-based, non-QC.
 * 4. PERMISSION already exists: VIEW_ONLY / VIEW_EDIT / HIDDEN. VIEW_EDIT is the DIY case
 *    this screen is for — the hook was already built on the admin side.
 *
 * Replaced by: /api/qc/*. Note that contract is NOT frozen — the admin module itself still
 * runs on mocks behind a USE_MOCK flag, so this screen is at the same maturity as ops, not
 * behind it.
 */

// The constant, courier-independent QC vocabulary. Order matches admin.
export const MASTER_PARAMS = [
  {id: 'img', name: 'Item Images (front/back)', desc: 'Front and back photos of the returned item.'},
  {id: 'dmg', name: 'Damage Check', desc: 'Agent confirms whether the item is physically damaged.'},
  {id: 'sku', name: 'SKU / EAN match', desc: 'Scanned or entered SKU/EAN matches the order.'},
  {id: 'brand', name: 'Brand Check', desc: 'Brand on the item matches the ordered brand.'},
  {id: 'qty', name: 'Quantity Check', desc: 'Number of units returned matches the order.'},
  {id: 'size', name: 'Size Match', desc: 'Item size matches the ordered size.'},
  {id: 'color', name: 'Colour Match', desc: 'Item colour matches the ordered colour.'},
  {id: 'seal', name: 'Tamper / Seal Intact', desc: 'Security seal or tamper-evident packaging is intact.'},
  {id: 'acc', name: 'Accessories Present', desc: 'All bundled accessories are present.'},
  {id: 'desc', name: 'Product Description', desc: 'Free-text product description captured at the doorstep.'},
]

export const paramName = (id) => (MASTER_PARAMS.find((p) => p.id === id) || {}).name || '(unmapped)'

export const QC_TYPES = [
  {
    key: 'standard',
    label: 'Standard QC',
    blurb: 'One set of checks, run on every reverse pickup regardless of why the buyer returned it.',
    bestFor: 'Simple catalogues where the failure modes are the same whatever the reason.',
  },
  {
    key: 'reason',
    label: 'Reason-based QC',
    blurb: 'Different checks per return reason. A damage claim gets photographed; a size complaint gets measured.',
    bestFor: 'Apparel and footwear, where "wrong size" and "damaged" need genuinely different evidence.',
  },
  {
    key: 'nonqc',
    label: 'Non-QC',
    blurb: 'No doorstep check. The parcel comes back and is inspected at your warehouse.',
    bestFor: 'Low-value items where a doorstep QC costs more than the write-off it prevents.',
  },
]

// What ops has granted this merchant. VIEW_EDIT is what makes this screen DIY rather than
// a read-out; the control already exists in the admin editor.
export const QC_PERMISSION = 'VIEW_EDIT' // VIEW_ONLY | VIEW_EDIT | HIDDEN

// Current setup. Reason-based, which is the interesting case to show.
export const rvpSetup = {
  type: 'reason',
  status: 'Live',
  updated: '18 Aug 2026',
  updatedBy: 'ops@prozo.com',
  // Selected params, and whether each is mandatory for the agent to complete.
  selected: ['img', 'dmg', 'sku', 'size'],
  mandatory: {img: true, dmg: true, sku: true, size: false},
  // HARD fails the QC outright; SOFT records the finding and lets the pickup proceed.
  // The distinction decides whether your agent walks away with the parcel or without it.
  checkType: {img: 'HARD', dmg: 'HARD', sku: 'HARD', size: 'SOFT'},
  reasons: [
    {id: 'r1', label: 'Product is damaged', cells: {img: true, dmg: true}},
    {id: 'r2', label: 'Wrong product delivered', cells: {img: true, sku: true}},
    {id: 'r3', label: 'Size or fit issue', cells: {img: true, size: true}},
    {id: 'r4', label: 'Changed my mind', cells: {img: true}},
  ],
}

/**
 * Return reasons must be unique verbatim — admin enforces this before save, because two
 * reasons reading the same thing make the reason-based matrix ambiguous and the resulting
 * QC unauditable.
 */
export const validateReasons = (reasons) => {
  const errors = []
  const seen = new Map()
  reasons.forEach((r, i) => {
    const label = (r.label || '').trim()
    if (!label) {
      errors.push({index: i, message: 'Reason text cannot be empty.'})
      return
    }
    const key = label.toLowerCase()
    if (seen.has(key)) {
      errors.push({index: i, message: `Duplicate of "${reasons[seen.get(key)].label}". Reasons must be unique.`})
    } else {
      seen.set(key, i)
    }
    if (!Object.values(r.cells || {}).some(Boolean)) {
      errors.push({index: i, message: 'At least one check must apply to this reason, or the agent has nothing to do.'})
    }
  })
  return errors
}
